import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/layouts/AppLayout";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import ShipperDashboard from "./pages/ShipperDashboard";
import CreateOrder from "./pages/shipper/CreateOrder";
import MyOrders from "./pages/shipper/MyOrders";
import OrderBids from "./pages/shipper/OrderBids";
import OrderTracking from "./pages/shipper/OrderTracking";
import ShipperBilling from "./pages/shipper/Billing";
import TransporterDashboard from "./pages/TransporterDashboard";
import TransporterMarketplace from "./pages/transporter/Marketplace";
import TransporterVehicles from "./pages/transporter/Vehicles";
import TransporterMissions from "./pages/transporter/Missions";
import TransporterBilling from "./pages/transporter/Billing";
import TransporterLiveMap from "./pages/transporter/LiveMap";
import ShipperLiveMap from "./pages/shipper/LiveMap";
import DriverMissions from "./pages/DriverMissions";
import OrderScan from "./pages/driver/OrderScan";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminUsers from "./pages/admin/Users";
import AdminOrders from "./pages/admin/Orders";
import AdminInvoices from "./pages/admin/Invoices";
import AdminDocuments from "./pages/admin/Documents";
import AdminSupport from "./pages/admin/Support";
import AdminCreateAccount from "./pages/admin/CreateAccount";
import InviteAccept from "./pages/InviteAccept";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import { CookieConsent } from "@/components/CookieConsent";
import LegalNotice from "./pages/LegalNotice";
import About from "./pages/About";
import DesignSystem from "./pages/DesignSystem";
import ResetPassword from "./pages/ResetPassword";
import { ScrollToTop } from "@/components/ScrollToTop";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Generic dashboard route - redirects to role-specific dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Navigate to="/shipper/dashboard" replace />
                </ProtectedRoute>
              }
            />
            
            {/* Role-specific dashboards */}
            <Route
              path="/shipper/dashboard"
              element={
                <ProtectedRoute allowedRoles={['shipper', 'admin']}>
                  <AppLayout>
                    <ShipperDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipper/orders"
              element={
                <ProtectedRoute allowedRoles={['shipper', 'admin']}>
                  <AppLayout>
                    <MyOrders />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipper/orders/create"
              element={
                <ProtectedRoute allowedRoles={['shipper', 'admin']}>
                  <AppLayout>
                    <CreateOrder />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipper/orders/:orderId/bids"
              element={
                <ProtectedRoute allowedRoles={['shipper', 'admin']}>
                  <AppLayout>
                    <OrderBids />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipper/orders/:orderId/tracking"
              element={
                <ProtectedRoute allowedRoles={['shipper', 'admin']}>
                  <AppLayout>
                    <OrderTracking />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipper/billing"
              element={
                <ProtectedRoute allowedRoles={['shipper', 'admin']}>
                  <AppLayout>
                    <ShipperBilling />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transporter/dashboard"
              element={
                <ProtectedRoute allowedRoles={['transporter', 'admin']}>
                  <AppLayout>
                    <TransporterDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transporter/marketplace"
              element={
                <ProtectedRoute allowedRoles={['transporter', 'admin']}>
                  <AppLayout>
                    <TransporterMarketplace />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transporter/vehicles"
              element={
                <ProtectedRoute allowedRoles={['transporter', 'admin']}>
                  <AppLayout>
                    <TransporterVehicles />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transporter/missions"
              element={
                <ProtectedRoute allowedRoles={['transporter', 'admin']}>
                  <AppLayout>
                    <TransporterMissions />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transporter/billing"
              element={
                <ProtectedRoute allowedRoles={['transporter', 'admin']}>
                  <AppLayout>
                    <TransporterBilling />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transporter/live-map"
              element={
                <ProtectedRoute allowedRoles={['transporter', 'admin']}>
                  <AppLayout>
                    <TransporterLiveMap />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipper/live-map"
              element={
                <ProtectedRoute allowedRoles={['shipper', 'admin']}>
                  <AppLayout>
                    <ShipperLiveMap />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/missions"
              element={
                <ProtectedRoute allowedRoles={['driver', 'admin']}>
                  <AppLayout>
                    <DriverMissions />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/order/:orderId"
              element={
                <ProtectedRoute allowedRoles={['driver', 'admin']}>
                  <AppLayout>
                    <OrderScan />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout>
                    <AdminDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout>
                    <AdminUsers />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout>
                    <AdminOrders />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invoices"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout>
                    <AdminInvoices />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/documents"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout>
                    <AdminDocuments />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/support"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout>
                    <AdminSupport />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/create"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AppLayout>
                    <AdminCreateAccount />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            
            
            {/* Placeholder protected routes */}
            <Route
              path="/shipments"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <div className="text-center py-12">
                      <h1 className="font-heading text-3xl font-semibold text-secondary mb-2">Expéditions</h1>
                      <p className="text-muted-foreground">Cette page sera développée prochainement</p>
                    </div>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <div className="text-center py-12">
                      <h1 className="font-heading text-3xl font-semibold text-secondary mb-2">Flotte</h1>
                      <p className="text-muted-foreground">Cette page sera développée prochainement</p>
                    </div>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <div className="text-center py-12">
                      <h1 className="font-heading text-3xl font-semibold text-secondary mb-2">Utilisateurs</h1>
                      <p className="text-muted-foreground">Cette page sera développée prochainement</p>
                    </div>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Settings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="/invite/:token" element={<InviteAccept />} />
            
            {/* Legal pages */}
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal-notice" element={<LegalNotice />} />
            <Route path="/about" element={<About />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/design-system" element={<DesignSystem />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieConsent />
        </BrowserRouter>

      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
